import {ReactNode, useEffect, useRef, useState} from "react";
import {Button, ButtonGroup, ButtonToolbar, Form} from "react-bootstrap";
import {Editor, EditorContent, useEditor, useEditorState} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import {toHtml} from "./sanitizeHtml";
import {ImageDialog} from "./ImageDialog";

// An uploaded image: its link, and its size in pixels.
export interface UploadedImage {
    src: string;
    width: number;
    height: number;
}

interface Props {
    id?: string;
    labelledBy?: string;
    value: string;
    onChange(value: string): void;
    // Allows images: uploads one and returns where it is. Without it, the editor has no images.
    onUploadImage?(file: File, onProgress: (fraction: number) => void): Promise<UploadedImage>;
    // The TipTap editor once it exists, for a page to insert things at the cursor.
    onEditor?(editor: Editor | null): void;
}

// The editor's HTML with a line break after each block, for reading in the source view.
const formatSource = (html: string) => html.replace(/(<\/(?:p|h[23]|li|ul|ol|blockquote)>|<(?:ul|ol|blockquote)>|<img[^>]*>)/g, '$1\n').trim();

// A WYSIWYG HTML editor. The value is HTML, or '' when empty; plain text from before is converted to paragraphs.
// The source view edits the HTML directly; it goes through the editor, so tags the editor doesn't support are dropped.
export function RichTextEditor({id, labelledBy, value, onChange, onUploadImage, onEditor}: Props) {
    // The last value this editor reported, so a value passed back in isn't reloaded (which would move the cursor).
    const emitted = useRef(value);
    const [source, setSource] = useState<string>();
    const editor = useEditor({
        // The CSP has no 'unsafe-inline' for styles: site.css carries TipTap's styles instead.
        injectCSS: false,
        extensions: [
            StarterKit.configure({
                // The page title is the h1.
                heading: { levels: [2, 3] },
                code: false,
                codeBlock: false,
                horizontalRule: false,
                link: { openOnClick: false, autolink: true, defaultProtocol: 'https' }
            }),
            // Images on their own line, as uploaded (never pasted in as data: URLs).
            ...(onUploadImage ? [Image.configure({ inline: false, allowBase64: false })] : [])
        ],
        content: toHtml(value),
        editorProps: {
            attributes: { class: 'form-control rich-text rich-text-editor', ...(id && { id }), ...(labelledBy && { 'aria-labelledby': labelledBy }) }
        },
        onUpdate: ({editor}) => {
            emitted.current = editor.isEmpty ? '' : editor.getHTML();
            onChange(emitted.current);
        }
    });

    useEffect(() => {
        onEditor?.(editor);
        return () => onEditor?.(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);

    useEffect(() => {
        if (editor && value !== emitted.current) {
            emitted.current = value;
            editor.commands.setContent(toHtml(value), { emitUpdate: false });
            setSource(x => x === undefined ? x : formatSource(value));
        }
    }, [editor, value]);

    const toggleSource = () => {
        if (source === undefined) {
            setSource(formatSource(emitted.current));
        } else {
            // The editor already holds the source as it understood it (see editSource).
            setSource(undefined);
            editor?.commands.focus();
        }
    };

    // Each change is loaded into the editor, so the value reported is always the editor's own HTML.
    const editSource = (html: string) => {
        setSource(html);
        editor?.commands.setContent(html, { emitUpdate: true });
    };

    return (
        <div>
            {editor && <Toolbar editor={editor} source={source !== undefined} onToggleSource={toggleSource} onUploadImage={onUploadImage}/>}
            {source !== undefined && (
                <Form.Control as="textarea" className="font-monospace rich-text-editor" rows={12} spellCheck={false}
                              id={id} aria-labelledby={labelledBy} value={source} onChange={e => editSource(e.target.value)}/>
            )}
            <div className={source !== undefined ? 'd-none' : undefined}>
                <EditorContent editor={editor}/>
            </div>
        </div>
    );
}

function Toolbar({editor, source, onToggleSource, onUploadImage}: { editor: Editor, source: boolean, onToggleSource(): void, onUploadImage?: Props['onUploadImage'] }) {
    const [imageDialog, setImageDialog] = useState(false);
    const state = useEditorState({
        editor,
        selector: ({editor}) => ({
            bold: editor.isActive('bold'),
            italic: editor.isActive('italic'),
            underline: editor.isActive('underline'),
            h2: editor.isActive('heading', { level: 2 }),
            h3: editor.isActive('heading', { level: 3 }),
            bulletList: editor.isActive('bulletList'),
            orderedList: editor.isActive('orderedList'),
            blockquote: editor.isActive('blockquote'),
            link: editor.isActive('link'),
            image: editor.isActive('image'),
            imageAlt: (editor.getAttributes('image').alt as string | undefined) ?? '',
            canUndo: editor.can().undo(),
            canRedo: editor.can().redo()
        })
    });

    const editLink = () => {
        const current = editor.getAttributes('link').href ?? '';
        const href = window.prompt('Link address (leave empty to remove the link)', current);
        if (href === null) {
            return;
        }
        const chain = editor.chain().focus().extendMarkRange('link');
        if (!href.trim()) {
            chain.unsetLink().run();
        } else {
            const url = /^([a-z][a-z0-9+.-]*:|\/|#)/i.test(href.trim()) ? href.trim() : `https://${href.trim()}`;
            chain.setLink({ href: url }).run();
        }
    };

    return (
        <ButtonToolbar className="gap-2 mb-2" aria-label="Formatting">
            <ButtonGroup size="sm">
                <Tool icon="type-bold" label="Bold" active={state.bold} disabled={source} onClick={() => editor.chain().focus().toggleBold().run()}/>
                <Tool icon="type-italic" label="Italic" active={state.italic} disabled={source} onClick={() => editor.chain().focus().toggleItalic().run()}/>
                <Tool icon="type-underline" label="Underline" active={state.underline} disabled={source} onClick={() => editor.chain().focus().toggleUnderline().run()}/>
                <Tool icon="link-45deg" label="Link" active={state.link} disabled={source} onClick={editLink}/>
            </ButtonGroup>
            <ButtonGroup size="sm">
                <Tool icon="type-h2" label="Heading" active={state.h2} disabled={source} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}/>
                <Tool icon="type-h3" label="Subheading" active={state.h3} disabled={source} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}/>
                <Tool icon="list-ul" label="Bulleted list" active={state.bulletList} disabled={source} onClick={() => editor.chain().focus().toggleBulletList().run()}/>
                <Tool icon="list-ol" label="Numbered list" active={state.orderedList} disabled={source} onClick={() => editor.chain().focus().toggleOrderedList().run()}/>
                <Tool icon="quote" label="Quote" active={state.blockquote} disabled={source} onClick={() => editor.chain().focus().toggleBlockquote().run()}/>
            </ButtonGroup>
            {onUploadImage && (
                <ButtonGroup size="sm">
                    <Tool icon="image" label={state.image ? 'Image description' : 'Image'} active={state.image} disabled={source} onClick={() => setImageDialog(true)}/>
                </ButtonGroup>
            )}
            <ButtonGroup size="sm">
                <Tool icon="eraser" label="Clear formatting" disabled={source} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}/>
                <Tool icon="arrow-counterclockwise" label="Undo" disabled={source || !state.canUndo} onClick={() => editor.chain().focus().undo().run()}/>
                <Tool icon="arrow-clockwise" label="Redo" disabled={source || !state.canRedo} onClick={() => editor.chain().focus().redo().run()}/>
            </ButtonGroup>
            <ButtonGroup size="sm" className="ms-auto">
                <Tool icon="code-slash" label="HTML source" active={source} onClick={onToggleSource}/>
            </ButtonGroup>
            {onUploadImage && (
                <ImageDialog show={imageDialog} editing={state.image ? { alt: state.imageAlt } : undefined} onUpload={onUploadImage}
                             onClose={() => setImageDialog(false)}
                             onInsert={(image, alt) => {
                                 editor.chain().focus().setImage({ src: image.src, alt, width: image.width, height: image.height } as never).run();
                                 setImageDialog(false);
                             }}
                             onDescribe={alt => {
                                 editor.chain().focus().updateAttributes('image', { alt }).run();
                                 setImageDialog(false);
                             }}/>
            )}
        </ButtonToolbar>
    );
}

function Tool({icon, label, active, disabled, onClick}: { icon: string, label: string, active?: boolean, disabled?: boolean, onClick(): void }): ReactNode {
    return (
        <Button variant="outline-secondary" title={label} aria-label={label} aria-pressed={active} active={active} disabled={disabled} onClick={onClick}>
            <i className={`bi bi-${icon}`}></i>
        </Button>
    );
}
